import { Button, Heading, Icon, Separator, Text, Stack } from '@sixfold/common-ui';
import { Localized } from '@sixfold/localization-component';
import { arrayToMultiMap } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import * as React from 'react';
import { Helmet } from 'react-helmet';

import { useProductName } from '../../lib/use_product_name';
import { Footer } from '../footer/footer';
import { MapPageFrame } from '../map_page_frame';

import styles from './styles.module.css';

export interface SharedView {
  sharedViewId: string;
  name: string;
  companyId?: string;
  companyName?: string;
}

interface Props {
  sharedViews: SharedView[];
  onNavigate: (sharedViewId?: string) => void;
}

export const SharedViewPicker: React.FC<Props> = ({ sharedViews, onNavigate }) => {
  const productName = useProductName();
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [selectedSharedViewId, setSelectedSharedViewId] = React.useState<string | undefined>(
    sharedViews[0]?.sharedViewId,
  );
  const showSharedViewList = sharedViews.length > 1;

  const sharedViewsByCompany = arrayToMultiMap(sharedViews, ({ companyId }) => companyId ?? 'no-company');

  return (
    <>
      <Localized
        id="sharedView.picker.page.title"
        defaultValue="Navigate to {PRODUCT_NAME}"
        variables={{ PRODUCT_NAME: productName }}>
        {(title) => (
          <Helmet>
            <title>{title}</title>
          </Helmet>
        )}
      </Localized>
      <MapPageFrame footer={<Footer />}>
        <Stack direction="column" gap="medium">
          {showSharedViewList && (
            <>
              <Heading size="xlarge" type="title">
                <Localized id="sharedView.picker.title">Select a shared view</Localized>
              </Heading>
              <Stack direction="column" gap="small">
                {[...sharedViewsByCompany.entries()].map(([companyKey, companySharedViews]) => (
                  <React.Fragment key={companyKey}>
                    <Separator start={companySharedViews[0].companyName} className={styles.separator} />
                    {companySharedViews.map(({ sharedViewId, name }) => {
                      const isSelected = selectedSharedViewId === sharedViewId;

                      return (
                        <Stack
                          alignItems="center"
                          justifyContent="space-between"
                          padding="medium"
                          radius="small"
                          key={sharedViewId}
                          className={classnames(styles.rowElement, {
                            [styles.selected]: isSelected,
                          })}
                          onClick={() => setSelectedSharedViewId(sharedViewId)}>
                          <Text size="medium" weight="bold">
                            {name}
                          </Text>
                          {isSelected ? <Icon name="small-check" /> : null}
                        </Stack>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Stack>
            </>
          )}
          <Button
            loading={isNavigating}
            size="medium"
            kind="primary"
            onClick={() => {
              setIsNavigating(true);
              onNavigate(selectedSharedViewId);
            }}>
            <Localized id="sharedView.picker.button.navigate">Navigate to {{ PRODUCT_NAME: productName }}</Localized>
          </Button>
        </Stack>
      </MapPageFrame>
    </>
  );
};
